import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Header from "../components/Header";
import { useCart } from "../context/CartContext";
import { pageColors } from "../mock";
import { CheckCircle2, Loader2, AlertTriangle } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { clear } = useCart();
  const navigate = useNavigate();
  const [state, setState] = useState("checking");
  const attempts = useRef(0);
  const cleared = useRef(false);

  useEffect(() => {
    document.title = "TMM|ORDER";
    if (!sessionId) { setState("error"); return; }
    let timer;
    const poll = async () => {
      attempts.current += 1;
      try {
        const res = await fetch(`${API}/api/payments/status/${sessionId}`);
        const data = await res.json();
        if (res.ok && data.payment_status === "paid") {
          if (!cleared.current) { cleared.current = true; clear(); }
          setState("paid");
          return;
        }
        if (data.status === "expired" || data.payment_status === "failed") {
          setState("error");
          return;
        }
      } catch { /* transient — keep polling */ }
      if (attempts.current >= 8) { setState("pending"); return; }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId, clear]);

  return (
    <>
      <Header hoverColor={pageColors.fashion} compact={false} />
      <div className="payment-page" data-testid="payment-success-page">
        {state === "checking" && (
          <>
            <Loader2 size={48} className="payment-spin" />
            <h1 className="payment-title" data-testid="payment-status-checking">Confirming your payment…</h1>
            <p className="payment-sub">Hold tight — we're checking with Stripe. This usually takes a few seconds.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <CheckCircle2 size={56} className="payment-icon-ok" />
            <h1 className="payment-title" data-testid="payment-status-paid">Order confirmed</h1>
            <p className="payment-sub">
              Payment received — thank you. A receipt is on its way to the email you used at checkout.
            </p>
          </>
        )}
        {state === "pending" && (
          <>
            <AlertTriangle size={48} className="payment-icon-warn" />
            <h1 className="payment-title" data-testid="payment-status-pending">Payment still processing</h1>
            <p className="payment-sub">
              We haven't gotten final confirmation yet. If you completed checkout, your receipt email will
              arrive once the payment settles — no need to pay again.
            </p>
          </>
        )}
        {state === "error" && (
          <>
            <AlertTriangle size={48} className="payment-icon-warn" />
            <h1 className="payment-title" data-testid="payment-status-error">Something went wrong</h1>
            <p className="payment-sub">We couldn't confirm this payment session. Your card was not charged if checkout didn't complete.</p>
          </>
        )}
        <div className="payment-actions">
          <button className="btn-solid" onClick={() => navigate("/shop")} data-testid="payment-continue-shopping">
            Continue shopping
          </button>
          <button className="btn-ghost" onClick={() => navigate("/home")} data-testid="payment-back-home">
            Back home
          </button>
        </div>
      </div>
    </>
  );
}
